/**
 * Generic Cookie Storage Utility
 * Provides reusable functions to manage cookies across the application
 * Supports both simple string values and complex JSON objects
 */

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
  if (typeof window === "undefined") return null;

  const nameEQ = name + "=";
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Set cookie with name, value, and expiry
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value (use JSON.stringify for objects)
 * @param {number} days - Days until expiry
 * @param {Object} options - Optional cookie attributes
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain
 * @param {boolean} options.secure - Secure flag (HTTPS only)
 * @param {string} options.sameSite - SameSite attribute ('Strict', 'Lax', or 'None')
 */
export const setCookie = (name, value, days, options = {}) => {
  if (typeof window === "undefined") return;

  const {
    path = "/",
    domain,
    secure,
    sameSite,
  } = options;

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  let cookie = `${name}=${value};expires=${expires.toUTCString()};path=${path}`;

  if (domain) cookie += `;domain=${domain}`;
  if (secure) cookie += `;secure`;
  if (sameSite) cookie += `;SameSite=${sameSite}`;

  document.cookie = cookie;
};

/**
 * Delete cookie by name
 * @param {string} name - Cookie name
 * @param {Object} options - Optional cookie attributes (should match the original cookie)
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain
 */
export const deleteCookie = (name, options = {}) => {
  if (typeof window === "undefined") return;

  const { path = "/", domain } = options;

  let cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${path}`;
  if (domain) cookie += `;domain=${domain}`;

  document.cookie = cookie;
};

/**
 * Get cookie value and parse as JSON
 * @param {string} name - Cookie name
 * @returns {Object|null} - Parsed JSON object or null if not found/invalid
 */
export const getCookieJSON = (name) => {
  const value = getCookie(name);
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(`Error parsing cookie "${name}" as JSON:`, error);
    return null;
  }
};

/**
 * Set cookie with JSON value
 * @param {string} name - Cookie name
 * @param {Object} value - Object to store as JSON
 * @param {number} days - Days until expiry
 * @param {Object} options - Optional cookie attributes
 */
export const setCookieJSON = (name, value, days, options = {}) => {
  try {
    const jsonString = JSON.stringify(value);
    setCookie(name, jsonString, days, options);
  } catch (error) {
    console.error(`Error stringifying value for cookie "${name}":`, error);
  }
};

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} - True if cookie exists
 */
export const hasCookie = (name) => {
  return getCookie(name) !== null;
};

// =============================================================================
// Document Access Specific Functions (Built on top of generic functions)
// =============================================================================

const DOCUMENT_ACCESS_EXPIRY_DAYS = 30;
const EXPIRY_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Check if user has already accessed a document type (within expiry period)
 * @param {string} docType - Document type (tds or msds)
 * @returns {boolean} - True if user has accessed within expiry period
 */
export const hasDocumentAccess = (docType) => {
  if (typeof window === "undefined") return false;

  const key = `doc_access`;
  const data = getCookieJSON(key);

  if (!data) return false;

  try {
    const { timestamp } = data;
    const now = Date.now();

    // Check if access is still valid
    if (now - timestamp < EXPIRY_DURATION) {
      return true;
    }

    // Access expired, remove cookie
    deleteCookie(key);
    return false;
  } catch (error) {
    console.error("Error checking document access:", error);
    deleteCookie(key);
    return false;
  }
};

/**
 * Set document access flag with current timestamp
 * @param {string} docType - Document type (tds or msds)
 */
export const setDocumentAccess = (docType) => {
  if (typeof window === "undefined") return;

  const key = `doc_access`;
  const data = {
    timestamp: Date.now(),
    accessed: true,
  };

  setCookieJSON(key, data, DOCUMENT_ACCESS_EXPIRY_DAYS);
};
